import React, { useState } from 'react';
import { X, FileCheck, AlertTriangle, CheckCircle2, Shield } from 'lucide-react';

/**
 * Content Owner Agreement Modal
 * Required for content owners before they can upload content
 */
function COAModal({ isOpen, onAccept, onClose, loading = false }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  if (!isOpen) return null;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Consider scrolled to bottom when within 50px of the end
    if (scrollHeight - scrollTop - clientHeight < 50) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (isChecked && hasScrolledToBottom) {
      onAccept();
    }
  };

  const canAccept = hasScrolledToBottom && isChecked;

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      data-testid="coa-modal-overlay"
    >
      <div 
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden'
        }}
        data-testid="coa-modal"
      >
        {/* Header */}
        <div style={{
          padding: '24px 28px',
          borderBottom: '1px solid #E5E7EB',
          backgroundColor: '#1B5E5F',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Shield size={26} style={{ color: '#C4E538' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              color: '#FFFFFF',
              margin: 0,
              marginBottom: '4px'
            }}>
              Content Owner Upload Declaration
            </h2>
            <p style={{ 
              fontSize: '13px', 
              color: 'rgba(255, 255, 255, 0.8)',
              margin: 0
            }}>
              Required before uploading content to the Compete Content Hub
            </p>
          </div>
        </div>

        {/* Agreement Content */}
        <div 
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 28px',
            backgroundColor: '#F9FAFB'
          }}
          onScroll={handleScroll}
          data-testid="coa-agreement-content"
        >
          {/* Introduction */}
          <p style={{ 
            fontSize: '14px', 
            color: '#374151', 
            lineHeight: '1.7',
            marginBottom: '24px'
          }}>
            By uploading content to the Compete Content Hub hosted on the LevelUp platform, 
            you ("Content Owner") agree to the following:
          </p>

          {/* Section 1: Authorization */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700'
              }}>1</span>
              Authorization
            </h3>
            <div style={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '8px', 
              padding: '16px',
              border: '1px solid #E5E7EB'
            }}>
              <p style={{ fontSize: '14px', color: '#4B5563', marginBottom: '8px', fontWeight: '500' }}>
                You confirm that:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#4B5563', fontSize: '14px', lineHeight: '1.8' }}>
                <li>You are an employee or authorized representative of Microsoft.</li>
                <li>You are duly authorized to upload and distribute this content on behalf of Microsoft.</li>
              </ul>
            </div>
          </div>

          {/* Section 2: Ownership & Rights */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700'
              }}>2</span>
              Ownership & Rights
            </h3>
            <div style={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '8px', 
              padding: '16px',
              border: '1px solid #E5E7EB'
            }}>
              <p style={{ fontSize: '14px', color: '#4B5563', marginBottom: '8px', fontWeight: '500' }}>
                You represent and warrant that:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#4B5563', fontSize: '14px', lineHeight: '1.8' }}>
                <li>Microsoft owns or has secured all necessary rights, licenses, and permissions for the uploaded content.</li>
                <li>The content does not infringe any third-party intellectual property rights.</li>
                <li>The content complies with Microsoft policies and applicable laws.</li>
              </ul>
            </div>
          </div>

          {/* Section 3: Responsibility for Content */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700'
              }}>3</span>
              Responsibility for Content
            </h3>
            <div style={{ 
              backgroundColor: '#FEF3C7', 
              borderRadius: '8px', 
              padding: '16px',
              border: '1px solid #F59E0B'
            }}>
              <p style={{ fontSize: '14px', color: '#92400E', marginBottom: '8px', fontWeight: '500' }}>
                You acknowledge that:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#92400E', fontSize: '14px', lineHeight: '1.8' }}>
                <li>Technofocus Pte Ltd operates the LevelUp platform solely as a hosting and technical service provider.</li>
                <li>Technofocus does not review, validate, approve, or govern the uploaded content.</li>
                <li>Technofocus bears no responsibility for the accuracy, legality, compliance, or appropriateness of the content.</li>
                <li><strong>All responsibility for the uploaded content remains solely with Microsoft and the uploading Content Owner.</strong></li>
              </ul>
            </div>
          </div>

          {/* Section 4: Indemnification */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700'
              }}>4</span>
              Indemnification
            </h3>
            <div style={{ 
              backgroundColor: '#FEE2E2', 
              borderRadius: '8px', 
              padding: '16px',
              border: '1px solid #EF4444'
            }}>
              <p style={{ fontSize: '14px', color: '#991B1B', lineHeight: '1.7' }}>
                You agree, on behalf of Microsoft, to <strong>indemnify and hold harmless</strong> Technofocus Pte Ltd, 
                its directors, employees, and affiliates from any claims, liabilities, damages, losses, or expenses 
                arising from or related to the uploaded content.
              </p>
            </div>
          </div>

          {/* Section 5: Removal Rights */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#1F2937',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '700'
              }}>5</span>
              Removal Rights
            </h3>
            <div style={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '8px', 
              padding: '16px',
              border: '1px solid #E5E7EB'
            }}>
              <p style={{ fontSize: '14px', color: '#4B5563', lineHeight: '1.7' }}>
                Technofocus reserves the right to remove content if required by law or upon direction from Microsoft, 
                but has no obligation to monitor content.
              </p>
            </div>
          </div>

          {/* Scroll Indicator */}
          {!hasScrolledToBottom && (
            <div style={{
              textAlign: 'center',
              padding: '16px',
              backgroundColor: '#EFF6FF',
              borderRadius: '8px',
              border: '1px solid #3B82F6',
              marginBottom: '16px'
            }}>
              <p style={{ fontSize: '13px', color: '#1D4ED8', margin: 0 }}>
                Please scroll to read the entire agreement before accepting
              </p>
            </div>
          )}
        </div>

        {/* Footer with Checkbox and Actions */}
        <div style={{
          padding: '20px 28px',
          borderTop: '1px solid #E5E7EB',
          backgroundColor: '#FFFFFF'
        }}>
          {/* Checkbox */}
          <label 
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              marginBottom: '20px',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed',
              opacity: hasScrolledToBottom ? 1 : 0.5,
              padding: '12px 16px',
              backgroundColor: isChecked ? '#F0FDF4' : '#F9FAFB',
              borderRadius: '8px',
              border: isChecked ? '2px solid #22C55E' : '2px solid #E5E7EB',
              transition: 'all 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => hasScrolledToBottom && setIsChecked(e.target.checked)}
              disabled={!hasScrolledToBottom}
              data-testid="coa-checkbox"
              style={{
                width: '20px',
                height: '20px',
                marginTop: '2px',
                accentColor: '#1B5E5F',
                cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
              }}
            />
            <span style={{ 
              fontSize: '14px', 
              color: isChecked ? '#166534' : '#374151',
              lineHeight: '1.5',
              fontWeight: isChecked ? '500' : '400'
            }}>
              I confirm that I am authorized by Microsoft and agree to the above terms.
            </span>
          </label>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            {onClose && (
              <button
                onClick={onClose}
                disabled={loading}
                data-testid="coa-cancel-button"
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#FFFFFF',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleAccept}
              disabled={!canAccept || loading}
              data-testid="coa-accept-button"
              style={{
                padding: '12px 32px',
                backgroundColor: canAccept ? '#1B5E5F' : '#9CA3AF',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: canAccept && !loading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                boxShadow: canAccept ? '0 4px 12px rgba(27, 94, 95, 0.3)' : 'none'
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#FFFFFF',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Processing...
                </>
              ) : (
                <>
                  <FileCheck size={18} />
                  Accept & Continue to Upload
                </>
              )}
            </button>
          </div>

          {/* Help Text */}
          {!canAccept && (
            <p style={{ 
              fontSize: '12px', 
              color: '#6B7280', 
              textAlign: 'center',
              marginTop: '12px',
              marginBottom: 0
            }}>
              {!hasScrolledToBottom 
                ? 'Please scroll through the entire agreement to enable acceptance'
                : 'Please check the confirmation box to accept'
              }
            </p>
          )}
        </div>

        {/* Animation styles */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default COAModal;
